import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { PreInvoiceConceptTableModel } from 'src/app/models/tables/invoicing/preinvoice-concept-table.model';
import { PreInvoiceTableModel } from 'src/app/models/tables/invoicing/preinvoice-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { PreinvoiceConceptService } from 'src/app/services/invoicing/preinvoice-concept.service';
import { PreinvoiceDetailService } from 'src/app/services/invoicing/preinvoice-detail.service';
import { PreinvoiceService } from 'src/app/services/invoicing/preinvoice.service';
import { PreInvoiceDetailTableModel } from '../../../../models/tables/invoicing/preinvoice-detail-table.model';

@Component({
  selector: 'app-pre-invoice',
  templateUrl: './pre-invoice.component.html',
  styleUrls: ['./pre-invoice.component.scss'],
  standalone: false
})
export class PreInvoiceComponent implements OnInit {

  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<PreInvoiceTableModel> = new PagedResponseModel<PreInvoiceTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  detailData: PagedResponseModel<PreInvoiceDetailTableModel> = new PagedResponseModel<PreInvoiceDetailTableModel>();
  conceptHeader: ColumnResponseModel[] = [];
  conceptData: PagedResponseModel<PreInvoiceConceptTableModel> = new PagedResponseModel<PreInvoiceConceptTableModel>();

  activeTabIndex = 0;
  page: number = 1;
  limit: number = 10;
  nameWindow: string = 'Prefacturas';
  columnSort: string | null = null;
  ascendant: boolean = true;
  filters: ColumnResponseModel[] = []
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;

  constructor(private preInvoiceService: PreinvoiceService,
    private preInvoiceConceptService: PreinvoiceConceptService,
    private preInvoiceDetailService: PreinvoiceDetailService,
    private headerService: HeaderService,
    private title: Title,
  ) { }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllPreInvoicesWithPagination(),
    ]);

  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    this.conceptHeader.length = this.conceptHeader.length === 1 ? 0 : this.conceptHeader.length;

    if (this.activeTabIndex === tabIndex) return;

    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;

    this.page = 1;

    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllPreInvoicesWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllPreInvoiceDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 2:
        await Promise.all([
          this.getColumnsForConceptConsultation(),
          this.getAllPreInvoiceConceptsWithPagination()
        ]);
        this.conceptData = {
          ...this.conceptData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllPreInvoicesWithPagination()
        break;
      case 1:
        await this.getAllPreInvoiceDetailsWithPagination()
        break;
      case 2:
        await this.getAllPreInvoiceConceptsWithPagination()
        break;
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
      case 2:
        await this.exportConceptConsultation();
        break;
    }
  }

  async exportGeneralConsultation() {
    try {
      const response = await firstValueFrom(this.preInvoiceService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Prefacturas.xlsx'; // El nombre del archivo
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error al exportar las prefacturas.', error);
    }
  }
  async exportDetailConsultation() {
    try {
      const response = await firstValueFrom(this.preInvoiceDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Prefacturas Detalle.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error al exportar las prefacturas detalle.', error);
    }
  }

  async exportConceptConsultation() {
    try {
      const response = await firstValueFrom(this.preInvoiceConceptService.exportConceptConsultation(this.filters, this.columnSort, this.ascendant));
      const blob = response;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'PreFacturas Concepto.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error al exportar las facturas concepto.', error);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);

    // Procesar cada filtro
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.preInvoiceService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllPreInvoicesWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.preInvoiceService.getAllPreInvoicesWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar las Prefacturas.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.preInvoiceDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllPreInvoiceDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.preInvoiceDetailService.getPreInvoicesDetailWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al buscar los detalles de las prefacturas.', error);
    }
  }

  async getColumnsForConceptConsultation() {
    try {
      if (this.conceptHeader.length === 0) {
        this.conceptHeader = await firstValueFrom(this.preInvoiceConceptService.getColumnsForConceptConsultation());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getAllPreInvoiceConceptsWithPagination() {
    try {
      this.conceptData = await firstValueFrom(this.preInvoiceConceptService.getAllPreInvoicesConceptWithPagination(this.page, this.limit, this.columnSort, this.ascendant, this.filters));

    } catch (error) {
      console.error('Error al buscar los conceptos de las prefacturas.', error);
    }
  }
}
